var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect } from 'react';
import { getActivePromo, formatEUR, formatPromoDate, DEFAULT_PROMO_MONTHS, PROMO_CONFIRM_DAYS } from '../utils/promo';
export var toDateInputValue = function (date) {
    var mm = String(date.getMonth() + 1).padStart(2, '0');
    var dd = String(date.getDate()).padStart(2, '0');
    return "".concat(date.getFullYear(), "-").concat(mm, "-").concat(dd);
};
export var defaultPromoEndDate = function () {
    var date = new Date();
    date.setMonth(date.getMonth() + DEFAULT_PROMO_MONTHS);
    return toDateInputValue(date);
};
// Promo runs through the end of the chosen day in the admin's local time.
export var promoEndFromDateInput = function (value) { return new Date("".concat(value, "T23:59:59")); };
export var daysFromToday = function (value) {
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((new Date("".concat(value, "T00:00:00")).getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
};
var formatNumber = function (n) { return String(Math.round(n * 100) / 100); };
var percentFromPrice = function (price, regularPrice) {
    var p = parseFloat(price);
    if (!(p > 0) || !(regularPrice > 0))
        return '';
    return formatNumber((1 - p / regularPrice) * 100);
};
var priceFromPercent = function (percent, regularPrice) {
    var pct = parseFloat(percent);
    if (!(pct > 0) || !(regularPrice > 0))
        return '';
    return (regularPrice * (1 - pct / 100)).toFixed(2);
};
export var emptyPromoForm = function () { return ({
    enabled: false,
    price: '',
    percent: '',
    endDate: defaultPromoEndDate(),
}); };
// An expired promo loads as "off" so saving the product clears it.
export var promoFormFromProduct = function (product) {
    var activePromo = getActivePromo(product);
    if (!activePromo)
        return emptyPromoForm();
    return {
        enabled: true,
        price: activePromo.price.toFixed(2),
        percent: percentFromPrice(activePromo.price.toString(), product.priceEUR),
        endDate: toDateInputValue(activePromo.endsAt),
    };
};
export var validatePromoForm = function (form, regularPrice) {
    if (!form.enabled)
        return null;
    var promoPrice = parseFloat(form.price);
    if (!(promoPrice > 0))
        return 'Promo price must be greater than 0';
    if (promoPrice >= regularPrice)
        return 'Promo price must be lower than the regular price';
    if (!form.endDate)
        return 'Promo end date is required';
    if (daysFromToday(form.endDate) < 0)
        return 'Promo end date cannot be in the past';
    return null;
};
export var confirmLongPromo = function (form) {
    if (!form.enabled)
        return true;
    var days = daysFromToday(form.endDate);
    if (days <= PROMO_CONFIRM_DAYS)
        return true;
    return window.confirm("Can you confirm you want the promo to last more than ".concat(PROMO_CONFIRM_DAYS, " days?\n\n") +
        "It is set to end on ".concat(formatPromoDate(promoEndFromDateInput(form.endDate)), " (").concat(days, " days from today)."));
};
// Empty values tell the server to remove an existing promo.
export var appendPromoToFormData = function (body, form) {
    if (form.enabled) {
        body.append('promoPriceEUR', (Math.round(parseFloat(form.price) * 100) / 100).toString());
        body.append('promoEndsAt', promoEndFromDateInput(form.endDate).toISOString());
    }
    else {
        body.append('promoPriceEUR', '');
        body.append('promoEndsAt', '');
    }
};
var PromoFields = function (_a) {
    var regularPrice = _a.regularPrice, value = _a.value, onChange = _a.onChange, _b = _a.showToggle, showToggle = _b === void 0 ? true : _b, _c = _a.idPrefix, idPrefix = _c === void 0 ? 'promo' : _c;
    // Keep the discount % in step with the promo price when the regular price is edited.
    useEffect(function () {
        if (value.price) {
            var percent = percentFromPrice(value.price, regularPrice);
            if (percent !== value.percent)
                onChange(__assign(__assign({}, value), { percent: percent }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regularPrice]);
    var promoPrice = parseFloat(value.price);
    var isValidPrice = promoPrice > 0 && regularPrice > 0 && promoPrice < regularPrice;
    var days = value.enabled && value.endDate ? daysFromToday(value.endDate) : 0;
    return (_jsxs("div", { className: "promo-section", children: [_jsxs("div", { className: "promo-header", children: [showToggle ? (_jsxs("label", { className: "promo-toggle", children: [_jsx("input", { type: "checkbox", checked: value.enabled, onChange: function (e) {
                                    var enabled = e.target.checked;
                                    onChange(__assign(__assign({}, value), { enabled: enabled, endDate: enabled && !value.enabled ? defaultPromoEndDate() : value.endDate }));
                                } }), "Promotional price"] })) : (_jsx("span", { className: "promo-toggle", children: "Promotional price" })), _jsxs("span", { className: "promo-regular-price", children: ["Regular price: ", _jsx("strong", { children: regularPrice > 0 ? formatEUR(regularPrice) : '—' })] })] }), value.enabled && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "promo-fields-row", children: [_jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "".concat(idPrefix, "-price"), children: "New price (EUR)" }), _jsx("input", { type: "number", id: "".concat(idPrefix, "-price"), value: value.price, onChange: function (e) { return onChange(__assign(__assign({}, value), { price: e.target.value, percent: percentFromPrice(e.target.value, regularPrice) })); }, step: "0.01", min: "0", placeholder: regularPrice > 0 ? (regularPrice * 0.8).toFixed(2) : 'e.g. 14.90' })] }), _jsx("div", { className: "promo-or", children: "or" }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "".concat(idPrefix, "-percent"), children: "Discount (%)" }), _jsx("input", { type: "number", id: "".concat(idPrefix, "-percent"), value: value.percent, onChange: function (e) { return onChange(__assign(__assign({}, value), { percent: e.target.value, price: priceFromPercent(e.target.value, regularPrice) })); }, step: "1", min: "1", max: "99", placeholder: "e.g. 20" })] }), _jsxs("div", { className: "form-group", children: [_jsx("label", { htmlFor: "".concat(idPrefix, "-end"), children: "Ends on" }), _jsx("input", { type: "date", id: "".concat(idPrefix, "-end"), value: value.endDate, min: toDateInputValue(new Date()), onChange: function (e) { return onChange(__assign(__assign({}, value), { endDate: e.target.value })); } })] })] }), _jsx("small", { className: "promo-hint", children: "Default is one month. The promo is active through the end of the chosen day." }), isValidPrice && value.endDate && (_jsxs("p", { className: "promo-preview", children: [_jsx("s", { children: formatEUR(regularPrice) }), " \u2192 ", formatEUR(promoPrice), " \u00B7 -", Math.round((1 - promoPrice / regularPrice) * 100), "% \u00B7 until ", formatPromoDate(promoEndFromDateInput(value.endDate))] })), value.price && !isValidPrice && (_jsx("p", { className: "promo-warning", children: "The new price must be above 0 and below the regular price." })), days > PROMO_CONFIRM_DAYS && (_jsxs("p", { className: "promo-warning", children: ["This promo lasts ", days, " days (more than ", PROMO_CONFIRM_DAYS, "). You'll be asked to confirm on save."] }))] }))] }));
};
export default PromoFields;
